// Object tracking on the environment radar.
//
// The radar tells her what lies in each direction *now*. Tracking is what turns
// those flashes into things: it matches this scan's contacts against the ones it
// saw a moment ago, so a warm body that drifts from NE to E over six seconds
// becomes one track with a bearing rate, a closing speed and a name — not six
// unrelated blips.
//
// The maths is a plain nearest-neighbour associator with a gate (a contact can
// only belong to a track if it is close in bearing AND range AND of the same
// kind), plus a hit/miss counter so a track has to be seen a few times before
// she trusts it and is dropped once it stops being seen. All local, all offline,
// persisted so a track survives a reload.

import { getOption, setOption } from "./persistOptions";
import { contacts, compassPoint, KIND_META, type ContactKind, type EnvContact } from "./envRadar";

export type Motion = "approaching" | "receding" | "crossing" | "holding";
export type TrackClass = "static" | "person" | "animal" | "vehicle" | "machine" | "unknown";

export interface TrackPoint { at: number; bearingDeg: number; rangeM: number }

export interface Track {
  id: string;
  kind: ContactKind;
  label: string;
  colour: string;
  bearingDeg: number;
  rangeM: number;
  /** Degrees per second, signed — positive is clockwise. */
  bearingRateDegS: number;
  /** Metres per second along the line of sight; negative is closing on us. */
  closingMs: number;
  /** Total speed across the ground, metres per second. */
  speedMs: number;
  motion: Motion;
  classification: TrackClass;
  firstAt: number;
  lastAt: number;
  hits: number;
  misses: number;
  confidence: number;
  history: TrackPoint[];
}

const TRACKS_KEY = "envradar:tracks";
const GATE_DEG = 28;        // a track can move this far in bearing between scans
const GATE_M = 5;           // …and this far in range
const MAX_MISSES = 5;       // scans without a sighting before we let it go
const DROP_MS = 90_000;     // …or this long, whichever comes first
const HISTORY = 24;         // points kept per track for the trail

let seq = 0;
const listeners = new Set<(t: Track[]) => void>();
let timer: ReturnType<typeof setInterval> | null = null;

/* ------------------------------------------------------------------ store */

export function tracks(): Track[] {
  const t = getOption<Track[]>(TRACKS_KEY, []);
  return Array.isArray(t) ? t.filter((x) => x && typeof x.id === "string") : [];
}

function save(list: Track[]): void {
  setOption(TRACKS_KEY, list);
  for (const fn of listeners) { try { fn(list); } catch { /* a bad listener shouldn't stop the rest */ } }
}

export function clearTracks(): void { save([]); }

export function subscribeTracks(fn: (t: Track[]) => void): () => void {
  listeners.add(fn);
  fn(tracks());
  return () => listeners.delete(fn);
}

/* ------------------------------------------------------- association maths */

/** Shortest signed angle from a to b, in degrees. */
export function angleDelta(a: number, b: number): number {
  let d = ((b - a + 540) % 360) - 180;
  if (d === -180) d = 180;
  return d;
}

/** Cost of claiming that this contact is the same object as this track. */
function cost(track: Track, c: EnvContact): number | null {
  if (track.kind !== c.kind) return null;
  const dBear = Math.abs(angleDelta(track.bearingDeg, c.bearingDeg));
  const dRange = Math.abs(track.rangeM - c.rangeM);
  if (dBear > GATE_DEG || dRange > GATE_M) return null;
  return dBear / GATE_DEG + dRange / GATE_M;
}

function classify(kind: ContactKind, speedMs: number, samples: number): TrackClass {
  if (kind === "machine" || kind === "wiring") return "machine";
  if (samples < 3) return "unknown";
  if (speedMs < 0.15) return "static";
  if (kind === "body") return speedMs > 3 ? "vehicle" : speedMs < 0.6 ? "animal" : "person";
  if (speedMs > 3.5) return "vehicle";
  if (speedMs > 0.4) return "person";
  return "static";
}

function motionOf(closingMs: number, tangentialMs: number): Motion {
  if (Math.abs(closingMs) < 0.12 && Math.abs(tangentialMs) < 0.12) return "holding";
  if (Math.abs(tangentialMs) > Math.abs(closingMs) * 1.4) return "crossing";
  return closingMs < 0 ? "approaching" : "receding";
}

/** Fit rate of change over the recent history rather than the last pair alone. */
function rates(h: TrackPoint[]): { bearingRateDegS: number; rangeRateMs: number } {
  const pts = h.slice(-6);
  if (pts.length < 2) return { bearingRateDegS: 0, rangeRateMs: 0 };
  const first = pts[0];
  const last = pts[pts.length - 1];
  const dt = (last.at - first.at) / 1000;
  if (dt <= 0.2) return { bearingRateDegS: 0, rangeRateMs: 0 };
  return {
    bearingRateDegS: +(angleDelta(first.bearingDeg, last.bearingDeg) / dt).toFixed(3),
    rangeRateMs: +((last.rangeM - first.rangeM) / dt).toFixed(3),
  };
}

/* ------------------------------------------------------------------- tick */

/** One tracking cycle: associate the live contacts with the running tracks. */
export function trackTick(): Track[] {
  const now = Date.now();
  const live = contacts({ minConfidence: 0.15 });
  const list = tracks();
  const claimed = new Set<string>();

  // Best-cost pairing, greedy — good enough for a handful of contacts.
  const pairs: Array<{ ti: number; c: EnvContact; cost: number }> = [];
  list.forEach((t, ti) => {
    for (const c of live) {
      const k = cost(t, c);
      if (k !== null) pairs.push({ ti, c, cost: k });
    }
  });
  pairs.sort((a, b) => a.cost - b.cost);

  const usedTrack = new Set<number>();
  for (const p of pairs) {
    if (usedTrack.has(p.ti) || claimed.has(p.c.id)) continue;
    usedTrack.add(p.ti);
    claimed.add(p.c.id);

    const t = list[p.ti];
    const history = [...t.history, { at: now, bearingDeg: p.c.bearingDeg, rangeM: p.c.rangeM }].slice(-HISTORY);
    const { bearingRateDegS, rangeRateMs } = rates(history);
    const tangential = (bearingRateDegS * Math.PI / 180) * p.c.rangeM;
    const speedMs = +Math.hypot(rangeRateMs, tangential).toFixed(2);

    list[p.ti] = {
      ...t,
      bearingDeg: p.c.bearingDeg,
      rangeM: p.c.rangeM,
      label: p.c.label,
      colour: p.c.colour,
      bearingRateDegS,
      closingMs: rangeRateMs,
      speedMs,
      motion: motionOf(rangeRateMs, tangential),
      classification: classify(t.kind, speedMs, t.hits + 1),
      lastAt: now,
      hits: t.hits + 1,
      misses: 0,
      confidence: +Math.min(1, p.c.confidence * (0.5 + Math.min(1, (t.hits + 1) / 6) * 0.5)).toFixed(3),
      history,
    };
  }

  // Unclaimed contacts become new tracks — but a burst (traffic going past
  // lights up several channels at once) must not balloon the list.
  let born = 0;
  for (const c of live) {
    if (claimed.has(c.id)) continue;
    if (born >= 12 || list.length >= 60) break;
    if (!Number.isFinite(c.bearingDeg) || !Number.isFinite(c.rangeM)) continue;
    born++;
    list.push({
      id: `trk-${now.toString(36)}-${(seq++).toString(36)}`,
      kind: c.kind,
      label: c.label,
      colour: c.colour,
      bearingDeg: c.bearingDeg,
      rangeM: c.rangeM,
      bearingRateDegS: 0,
      closingMs: 0,
      speedMs: 0,
      motion: "holding",
      classification: "unknown",
      firstAt: now,
      lastAt: now,
      hits: 1,
      misses: 0,
      confidence: +(c.confidence * 0.5).toFixed(3),
      history: [{ at: now, bearingDeg: c.bearingDeg, rangeM: c.rangeM }],
    });
  }


  // Age out the ones she can no longer see.
  const kept = list
    .map((t, i) => (usedTrack.has(i) ? t : { ...t, misses: t.misses + 1 }))
    .filter((t) => t.misses <= MAX_MISSES && now - t.lastAt < DROP_MS);

  kept.sort((a, b) => b.confidence * (b.speedMs + 0.2) - a.confidence * (a.speedMs + 0.2));
  save(kept.slice(0, 60));
  return kept;
}

/** Run the tracker in the background while a radar screen is open. */
export function startTracking(periodMs = 1500): () => void {
  if (timer) return () => { /* already running; owner stops it */ };
  timer = setInterval(() => { try { trackTick(); } catch { /* radar not ready */ } }, periodMs);
  trackTick();
  return stopTracking;
}

export function stopTracking(): void {
  if (timer) { clearInterval(timer); timer = null; }
}

export function isTracking(): boolean { return timer !== null; }

/* ---------------------------------------------------------------- read-out */

/** Tracks worth mentioning: moving, confident, and heading our way first. */
export function movers(minSpeed = 0.2): Track[] {
  return tracks()
    .filter((t) => t.speedMs >= minSpeed && t.confidence >= 0.2)
    .sort((a, b) => (a.motion === "approaching" ? -1 : 1) - (b.motion === "approaching" ? -1 : 1) || b.speedMs - a.speedMs);
}

/** Things closing on us fast or already close — worth a nudge, not an alarm. */
export function trackAlerts(): Array<{ track: Track; text: string; urgency: "watch" | "close" }> {
  const out: Array<{ track: Track; text: string; urgency: "watch" | "close" }> = [];
  for (const t of tracks()) {
    // Seen at least three times: a car passing the window is one spike, not a thing.
    if (t.hits < 3 || t.confidence < 0.25 || t.motion !== "approaching") continue;
    const ttc = t.closingMs < -0.05 ? t.rangeM / Math.abs(t.closingMs) : Infinity;
    if (t.rangeM <= 2.5) {
      out.push({ track: t, urgency: "close", text: `${t.label.toLowerCase()} within ${t.rangeM} m to your ${compassPoint(t.bearingDeg)}` });
    } else if (ttc < 8) {
      out.push({ track: t, urgency: "watch", text: `${t.label.toLowerCase()} closing from ${compassPoint(t.bearingDeg)}, about ${Math.round(ttc)} s out` });
    }
  }
  return out.sort((a, b) => a.track.rangeM - b.track.rangeM).slice(0, 5);
}

export function describeTracks(): string {
  const m = movers();
  if (!m.length) return "";
  const bits = m.slice(0, 4).map((t) => {
    const cls = t.classification === "unknown" ? t.label.toLowerCase() : t.classification;
    const verb = t.motion === "approaching" ? "coming toward me" : t.motion === "receding" ? "moving away" : "crossing";
    return `${KIND_META[t.kind].label.toLowerCase()} (${cls}) ${compassPoint(t.bearingDeg)} at ${t.rangeM} m, ${verb} at about ${t.speedMs.toFixed(1)} m/s`;
  });
  return `Moving things I'm tracking: ${bits.join("; ")}.`;
}

export function exportTracks(): string {
  return JSON.stringify({ at: Date.now(), tracks: tracks(), alerts: trackAlerts(), narrative: describeTracks() }, null, 2);
}
